import Alpine from 'alpinejs';
import axios from '../config/axios.js';

const API = '/api/v1/ads';

let configCache = null;
let configPromise = null;

async function fetchAdConfig() {
  if (configCache) return configCache;
  if (!configPromise) {
    configPromise = axios.get(`${API}/config`, { params: { platform: 'web' } })
      .then(r => {
        configCache = r.data.data || {};
        return configCache;
      })
      .catch(() => ({ enabled: false, placements: [] }));
  }
  return configPromise;
}

function findPlacement(config, key) {
  return (config?.placements || []).find(p => p.key === key) || null;
}

function createAdSlot() {
  return {
    placementKey: '',
    loading: true,
    placement: null,
    visible: false,

    async init() {
      this.placementKey = this.$el.dataset.placementKey || '';
      const cfg = await fetchAdConfig();
      this.placement = findPlacement(cfg, this.placementKey);
      this.visible = !!(cfg.enabled && this.placement);
      this.loading = false;
      if (this.visible) {
        this.track('impression');
      }
    },

    async track(eventType) {
      if (!this.placement) return;
      try {
        await axios.post(`${API}/track`, {
          placement_key: this.placementKey,
          event_type: eventType,
          platform: 'web',
          network: this.placement.network,
        });
      } catch { /* silent */ }
    },

    onClick() {
      this.track('click');
      const url = this.placement?.house?.url;
      if (url) window.location.href = url;
    },
  };
}

function createAdRewarded() {
  return {
    placementKey: '',
    loading: false,
    placement: null,
    session: null,
    watchSec: 0,
    watchTimer: null,
    busy: false,
    modalOpen: false,

    async initRewarded() {
      this.placementKey = this.$el.dataset.placementKey || '';
      if (!this.placementKey) return;
      const cfg = await fetchAdConfig();
      this.placement = findPlacement(cfg, this.placementKey);
    },

    get rewardLabel() {
      return this.placement?.reward?.label || 'Klaim hadiah';
    },

    async startRewarded() {
      if (this.busy) return;
      this.busy = true;
      try {
        const { data } = await axios.post(`${API}/reward/start`, {
          placement_key: this.placementKey,
          platform: 'web',
        });
        this.session = data.data;
        this.watchSec = 0;
        this.modalOpen = true;
        const min = Number(this.session?.min_watch_sec || 15);
        this.watchTimer = setInterval(() => {
          this.watchSec += 1;
          if (this.watchSec >= min) clearInterval(this.watchTimer);
        }, 1000);
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Iklan tidak tersedia.', 'error');
      } finally {
        this.busy = false;
      }
    },

    get canClaim() {
      const min = Number(this.session?.min_watch_sec || 15);
      return this.watchSec >= min;
    },

    async completeRewarded() {
      if (!this.session?.session_token || !this.canClaim || this.busy) return;
      this.busy = true;
      try {
        const { data } = await axios.post(`${API}/reward/complete`, {
          session_token: this.session.session_token,
          platform: 'web',
        });
        this.modalOpen = false;
        window.toast?.(data.message || 'Hadiah diklaim!', 'success');
        window.dispatchEvent(new CustomEvent('ads:reward-claimed', { detail: data.data }));
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal klaim hadiah.', 'error');
      } finally {
        this.busy = false;
        if (this.watchTimer) clearInterval(this.watchTimer);
      }
    },

    closeModal() {
      this.modalOpen = false;
      if (this.watchTimer) clearInterval(this.watchTimer);
    },
  };
}

Alpine.data('adSlot', createAdSlot);
Alpine.data('adRewarded', createAdRewarded);

export { fetchAdConfig, createAdSlot, createAdRewarded };
